<script setup lang="ts">
import { ref, watch } from 'vue'
import { 
  VueFlow, 
  useVueFlow, 
  Handle, 
  Position, 
  BaseEdge, 
  EdgeLabelRenderer, 
  getBezierPath
} from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Square, Trash2, Copy, Check, RefreshCw, Code2, Diamond, Circle, Cylinder, Hexagon, Component as ComponentIcon, Layers } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'save', mermaidCode: string): void
}>()

const nodes = ref<any[]>([])
const edges = ref<any[]>([])
const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)
const flowchartDirection = ref('TD')

// Inicializar lienzo
const initNodes = () => {
  nodes.value = [
    { id: 'A', type: 'flowNode', position: { x: 200, y: 50 }, data: { id: 'A', label: 'Inicio', shape: 'rounded', group: '', color: '' } },
    { id: 'B', type: 'flowNode', position: { x: 200, y: 150 }, data: { id: 'B', label: 'Proceso', shape: 'rect', group: 'Subsistema', color: '#bbf7d0' } }
  ]
  edges.value = [
    { id: 'e-A-B', source: 'A', target: 'B', type: 'custom', data: { relation: '-->', label: 'inicia' } }
  ]
}

// Inicializar al abrir
watch(() => props.open, (val) => {
  if (!val) {
    // Limpiar estado al cerrar para que no contamine la siguiente apertura
    nodes.value = []
    edges.value = []
    editableCode.value = ''
    return
  }

  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && nodes.value.length === 0) initNodes()
}, { immediate: true })

const { onConnect } = useVueFlow()

const handleConnect = (params: any) => {
  edges.value.push({
    id: `e-${params.source}-${params.target}-${Date.now()}`,
    source: params.source,
    target: params.target,
    type: 'custom',
    data: { 
      relation: '-->', // Flecha por defecto
      label: '',
    }
  })
}

const addFlowNode = (shape: string) => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  let nextId = letters[nodes.value.length % 26]
  let counter = 1
  while (nodes.value.some(n => n.id === nextId)) {
    nextId = `${letters[nodes.value.length % 26]}${counter++}`
  }

  nodes.value.push({
    id: nextId,
    type: 'flowNode',
    position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
    data: { id: nextId, label: 'Nuevo Nodo', shape, group: '', color: '' }
  })
}

const deleteNode = (id: string) => {
  nodes.value = nodes.value.filter(n => n.id !== id)
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id)
}

const deleteEdge = (id: string) => {
  edges.value = edges.value.filter(e => e.id !== id)
}

const generateCodeString = () => {
  let mermaid = `\n\`\`\`mermaid\n`
  
  // Guardar metadata de posiciones (oculto en comentario Mermaid)
  const positions: Record<string, any> = {}
  nodes.value.forEach(node => {
    positions[node.id] = { x: Math.round(node.position.x), y: Math.round(node.position.y) }
  })
  mermaid += `%% {"positions": ${JSON.stringify(positions)}}\n`
  
  mermaid += `flowchart ${flowchartDirection.value}\n`
  
  const declaredNodes = new Set<string>()
  const groups = new Map<string, any[]>()
  const ungrouped: any[] = []

  // Agrupar nodos
  nodes.value.forEach(node => {
    const groupName = node.data.group?.trim()
    if (groupName) {
      if (!groups.has(groupName)) groups.set(groupName, [])
      groups.get(groupName)!.push(node)
    } else {
      ungrouped.push(node)
    }
  })

  const formatNode = (node: any) => {
    let line = `  ${node.data.id}`
    const label = node.data.label
    
    if (node.data.shape === 'rect') line += `[${label}]`
    else if (node.data.shape === 'rounded') line += `(${label})`
    else if (node.data.shape === 'diamond') line += `{${label}}`
    else if (node.data.shape === 'cylinder') line += `[(${label})]`
    else if (node.data.shape === 'subroutine') line += `[[${label}]]`
    else if (node.data.shape === 'hexagon') line += `{{${label}}}`
    else if (node.data.shape === 'parallelogram') line += `[/${label}/]`
    else line += `[${label}]`
    
    declaredNodes.add(node.data.id)
    return line
  }

  // Nodos sin grupo
  ungrouped.forEach(node => {
    mermaid += formatNode(node) + '\n'
  })

  // Subgrafos
  groups.forEach((groupNodes, groupName) => {
    mermaid += `  subgraph ${groupName.replace(/\s+/g, '_')} [${groupName}]\n`
    groupNodes.forEach(node => {
      mermaid += `  ${formatNode(node)}\n`
    })
    mermaid += `  end\n`
  })

  // Declarar los edges
  edges.value.forEach(edge => {
    const rel = edge.data?.relation || '-->'
    let lbl = edge.data?.label?.trim()
    
    if (edge.source && edge.target) {
      let line = `  ${edge.source}`
      if (lbl) {
        if (rel === '-->') line += ` -->|${lbl}| `
        else if (rel === '-.->') line += ` -.->|${lbl}| `
        else if (rel === '===') line += ` ===|${lbl}| `
        else line += ` ${rel}|${lbl}| `
      } else {
        line += ` ${rel} `
      }
      line += `${edge.target}`
      mermaid += line + '\n'
    }
  })

  // Estilos
  nodes.value.forEach(node => {
    if (node.data.color && node.data.color.trim()) {
      mermaid += `  style ${node.data.id} fill:${node.data.color},stroke:#333,stroke-width:2px,color:#000\n`
    }
  })

  mermaid += '```\n'
  return mermaid
}

// Escuchar cambios en el UI visual para actualizar el código texto
watch([nodes, edges, flowchartDirection], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

// Parsear Mermaid a VueFlow
const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const parsedNodes = new Map<string, any>()
    const parsedEdges: any[] = []
    let savedPositions: Record<string, any> | null = null

    // Helper to get or create node
    const getNodeData = (id: string) => {
      if (!parsedNodes.has(id)) {
        parsedNodes.set(id, { id, label: id, shape: 'rect', group: '', color: '' })
      }
      return parsedNodes.get(id)
    }

    // RegEx para parsear definicion de nodos: A[Label], A(Label), A[[Label]], A{{Label}}, A[/Label/]
    const nodeRegex = /^(\w+)\s*(?:(\[\(|\[\[|\{\{|\[\/|\[|\(|\{)(.*?)(?:\)\]|\]\]|\}\}|\\\]|\/\]|\]|\)|\}))?$/
    
    let currentSubgraph = ''

    lines.forEach(line => {
      line = line.trim()
      
      // Parsear JSON oculto de posiciones
      if (line.startsWith('%% {"positions":')) {
        try {
          savedPositions = JSON.parse(line.replace(/^%% /, '')).positions
        } catch (e) {
          console.error('Error parseando metadata de posiciones', e)
        }
        return
      }
      
      if (!line || line.startsWith('```') || line.startsWith('%%')) return
      
      // Parsear dirección
      if (line.startsWith('flowchart ')) {
        const dir = line.replace('flowchart ', '').trim()
        if (['TD', 'TB', 'LR', 'RL', 'BT'].includes(dir)) {
          flowchartDirection.value = dir
        }
        return
      }

      // Parsear Subgraphs
      if (line.startsWith('subgraph ')) {
        const match = line.match(/^subgraph\s+([^\s[]+)(?:\s+\[(.*?)\])?$/)
        if (match) {
           currentSubgraph = match[2] || match[1] // usa el label o el id
        }
        return
      }
      if (line === 'end' && currentSubgraph !== '') {
        currentSubgraph = ''
        return
      }

      // Parsear Estilos
      if (line.startsWith('style ')) {
        const styleMatch = line.match(/^style\s+(\w+)\s+fill:([^,]+)/)
        if (styleMatch) {
          const id = styleMatch[1]
          const color = styleMatch[2]
          getNodeData(id).color = color
        }
        return
      }

      // Tratar de encontrar una relación de conexión
      const edgeMatches = line.split(/(-->|-\.\.->|-\.->|===|---)/)
      
      // Si hay más de un elemento al hacer split, encontramos una flecha
      if (edgeMatches.length >= 3) {
        // En Mermaid flowchart a veces hay labels en la flecha, e.g. A -->|texto| B
        let leftPart = edgeMatches[0].trim()
        let rel = edgeMatches[1].trim()
        let rightPart = edgeMatches.slice(2).join('').trim()
        
        let label = ''
        
        // Detectar si rightPart empieza con |texto|
        const labelMatch = rightPart.match(/^\|([^|]+)\|\s*(.+)$/)
        if (labelMatch) {
          label = labelMatch[1]
          rightPart = labelMatch[2].trim()
        }

        // Parsear nodo izquierdo (solo el ID si fue declarado)
        const leftNodeMatch = leftPart.match(nodeRegex)
        const leftId = leftNodeMatch ? leftNodeMatch[1] : leftPart.split(' ')[0]

        // Parsear nodo derecho
        const rightNodeMatch = rightPart.match(nodeRegex)
        const rightId = rightNodeMatch ? rightNodeMatch[1] : rightPart.split(' ')[0]

        // Registrar nodos (en caso no hayan sido declarados sueltos)
        const registerNode = (match: any, id: string) => {
          if (match && match[2]) {
             const shapeSymbol = match[2]
             let shape = 'rect'
             if (shapeSymbol === '(') shape = 'rounded'
             if (shapeSymbol === '{') shape = 'diamond'
             if (shapeSymbol === '[(') shape = 'cylinder'
             if (shapeSymbol === '[[') shape = 'subroutine'
             if (shapeSymbol === '{{') shape = 'hexagon'
             if (shapeSymbol === '[/') shape = 'parallelogram'
             const n = getNodeData(id)
             n.label = match[3]
             n.shape = shape
             if (currentSubgraph) n.group = currentSubgraph
          } else {
             const n = getNodeData(id)
             if (currentSubgraph) n.group = currentSubgraph
          }
        }
        
        registerNode(leftNodeMatch, leftId)
        registerNode(rightNodeMatch, rightId)

        parsedEdges.push({
          source: leftId, target: rightId, relation: rel, label
        })
        return
      }

      // Si no es un edge, podría ser una definición de nodo suelta
      const singleNodeMatch = line.match(nodeRegex)
      if (singleNodeMatch && singleNodeMatch[2]) {
        const id = singleNodeMatch[1]
        const shapeSymbol = singleNodeMatch[2]
        const label = singleNodeMatch[3]
        
        let shape = 'rect'
        if (shapeSymbol === '(') shape = 'rounded'
        if (shapeSymbol === '{') shape = 'diamond'
        if (shapeSymbol === '[(') shape = 'cylinder'
        if (shapeSymbol === '[[') shape = 'subroutine'
        if (shapeSymbol === '{{') shape = 'hexagon'
        if (shapeSymbol === '[/') shape = 'parallelogram'

        const n = getNodeData(id)
        n.label = label
        n.shape = shape
        if (currentSubgraph) n.group = currentSubgraph
      }
    })

    // Reconstruir visuales
    const newNodes: any[] = []
    let currentX = 100, currentY = 100
    
    parsedNodes.forEach((data, id) => {
      let pos = { x: currentX, y: currentY }
      
      // 1. Usar coordenadas guardadas en el JSON oculto
      if (savedPositions && savedPositions[id]) {
        pos = savedPositions[id]
      } 
      // 2. Fallback: mantener posición existente si editaba en vivo
      else {
        const existing = nodes.value.find(n => n.id === id)
        if (existing) {
          pos = existing.position
        } else {
          currentX += 80; currentY += 80
        }
      }
      
      newNodes.push({
        id, type: 'flowNode', position: pos,
        data: { id, label: data.label, shape: data.shape, group: data.group, color: data.color }
      })
    })

    const newEdges = parsedEdges.map((e, i) => ({
      id: `e-${e.source}-${e.target}-${i}`,
      source: e.source,
      target: e.target,
      type: 'custom',
      data: { relation: e.relation, label: e.label }
    }))

    nodes.value = newNodes
    edges.value = newEdges
  } catch (e) {
    console.error('Error parseando Mermaid:', e)
  }
  
  // Pequeño delay para reactivar el watcher sin loop
  setTimeout(() => isParsing.value = false, 100)
}

let debounceTimer: ReturnType<typeof setTimeout>
const onCodeChange = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    syncToVisual()
  }, 400) // 400ms delay para auto-sincronizar mientras escribe
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
  initNodes()
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy text', err)
  }
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Flujo (Flowchart)"
    description="Construye flujos de trabajo. Selecciona la forma del nodo, escribe su texto, y únelos usando los puntos de anclaje (arriba/abajo/lados)."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="generateMermaid"
  >
    <template #icon>
      <Workflow class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <div class="flex items-center gap-2 border-r pr-2 mr-1">
        <select v-model="flowchartDirection" class="text-xs border rounded p-1 bg-background focus:outline-none" title="Dirección del Flujo">
          <option value="TD">Top-Down (Arriba a Abajo)</option>
          <option value="LR">Left-Right (Izq a Der)</option>
          <option value="RL">Right-Left (Der a Izq)</option>
          <option value="BT">Bottom-Top (Abajo a Arriba)</option>
        </select>
      </div>
      <div class="flex flex-wrap gap-1">
        <Button size="sm" @click="addFlowNode('rect')" class="h-7 text-xs"><Square class="w-3 h-3 mr-1" /> Proceso</Button>
        <Button size="sm" @click="addFlowNode('rounded')" class="h-7 text-xs"><Square class="w-3 h-3 mr-1 rounded-md" /> Terminal</Button>
        <Button size="sm" @click="addFlowNode('diamond')" class="h-7 text-xs"><Diamond class="w-3 h-3 mr-1" /> Decisión</Button>
        <Button size="sm" @click="addFlowNode('cylinder')" class="h-7 text-xs"><Database class="w-3 h-3 mr-1" /> Datos</Button>
        <Button size="sm" @click="addFlowNode('circle')" class="h-7 text-xs"><Circle class="w-3 h-3 mr-1" /> Círculo</Button>
        <Button size="sm" @click="addFlowNode('subroutine')" class="h-7 text-xs"><ComponentIcon class="w-3 h-3 mr-1" /> Subrutina</Button>
        <Button size="sm" @click="addFlowNode('hexagon')" class="h-7 text-xs"><Hexagon class="w-3 h-3 mr-1" /> Hexágono</Button>
        <Button size="sm" @click="addFlowNode('parallelogram')" class="h-7 text-xs"><Layers class="w-3 h-3 mr-1" /> Paralelo</Button>
      </div>
    </template>

    <template #canvas>
      <VueFlow 
        v-model:nodes="nodes" 
        v-model:edges="edges"
        @connect="handleConnect"
        :default-zoom="1"
      >
        <Background />
        <Controls />

        <!-- Node Template para Flowchart -->
        <template #node-flowNode="nodeProps">
          <div class="relative group p-1 flex flex-col items-center justify-center font-sans text-sm">
            
            <!-- Grupo Subgraph / Color Picker Tooltip -->
            <div class="absolute -top-10 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground text-[10px] p-1 rounded shadow flex gap-1 items-center opacity-0 group-hover:opacity-100 transition-opacity z-30 pointer-events-none group-hover:pointer-events-auto">
              <input v-model="nodeProps.data.group" class="bg-background border rounded px-1 w-20" placeholder="Subgrafo..." title="Nombre del Subgrafo (Grupo)" />
              <input type="color" v-model="nodeProps.data.color" class="w-5 h-5 rounded cursor-pointer border-0 p-0" title="Color de Fondo" />
            </div>

            <div 
              class="border-2 border-primary shadow-sm flex items-center justify-center p-3 min-w-[120px] transition-all"
              :class="{
                'bg-card': !nodeProps.data.color,
                'rounded-sm': nodeProps.data.shape === 'rect',
                'rounded-[30px] px-6': nodeProps.data.shape === 'rounded',
                'rounded-full px-6': nodeProps.data.shape === 'circle',
                'rotate-45 w-24 h-24': nodeProps.data.shape === 'diamond',
                'rounded-[100%_100%_0_0/20px_20px_0_0] border-b-0 pb-6': nodeProps.data.shape === 'cylinder',
                'border-x-8': nodeProps.data.shape === 'subroutine',
                'clip-hexagon': nodeProps.data.shape === 'hexagon',
                'skew-x-[-15deg] px-8': nodeProps.data.shape === 'parallelogram'
              }"
              :style="[
                nodeProps.data.shape === 'cylinder' ? 'box-shadow: 0 8px 0 -2px hsl(var(--primary)); margin-bottom: 8px;' : '',
                nodeProps.data.color ? `background-color: ${nodeProps.data.color}; border-color: #333; color: #000;` : ''
              ]"
            >
              <input 
                v-model="nodeProps.data.label" 
                class="bg-transparent text-center font-semibold focus:outline-none focus:ring-1 focus:ring-primary/50 w-full" 
                :class="{
                  'rotate-[-45deg]': nodeProps.data.shape === 'diamond', 
                  'w-16 h-16 text-xs': nodeProps.data.shape === 'diamond',
                  'skew-x-[15deg]': nodeProps.data.shape === 'parallelogram',
                  'text-black': nodeProps.data.color
                }"
                placeholder="Texto" 
              />
            </div>
            
            <!-- Handles para conexiones (4 lados) -->
            <Handle type="target" :position="Position.Top" id="top" />
            <Handle type="source" :position="Position.Bottom" id="bottom" />
            <Handle type="target" :position="Position.Left" id="left" />
            <Handle type="source" :position="Position.Right" id="right" />

            <!-- Botón Borrar global -->
            <div class="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button @click.stop="deleteNode(nodeProps.id)" class="bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 shadow-sm" title="Eliminar">×</button>
            </div>
          </div>
        </template>

        <!-- Plantilla para Conexiones (Flechas) Flowchart -->
        <template #edge-custom="edgeProps">
          <BaseEdge 
            :id="edgeProps.id" 
            :path="getBezierPath(edgeProps)[0]" 
            class="vue-flow__edge-path" 
            :style="{ strokeWidth: 2, stroke: 'hsl(var(--primary))' }"
          />
          <EdgeLabelRenderer>
            <div
              :style="{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${getBezierPath(edgeProps)[1]}px,${getBezierPath(edgeProps)[2]}px)`,
                pointerEvents: 'all',
              }"
              class="nodrag nopan bg-card border shadow-sm rounded p-1 group z-10 flex flex-col gap-1 w-[120px]"
            >
              <!-- Configuración de Relación y Label -->
              <div class="flex items-center gap-1">
                <select v-model="edgeProps.data.relation" class="flex-1 text-[10px] border rounded p-1 bg-background focus:outline-none" title="Estilo de Flecha">
                  <option value="-->">Sólida --&gt;</option>
                  <option value="-.->">Punteada -.-&gt;</option>
                  <option value="===">Gruesa ===</option>
                  <option value="---">Línea ---</option>
                </select>
              </div>
              
              <input 
                v-model="edgeProps.data.label" 
                class="w-full bg-background text-center border rounded p-1 text-xs text-foreground focus:outline-none"
                placeholder="Etiqueta..."
              />

              <!-- Botón eliminar -->
              <button 
                @click="deleteEdge(edgeProps.id)" 
                class="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Eliminar Conexión"
              >×</button>
            </div>
          </EdgeLabelRenderer>
        </template>

      </VueFlow>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>

<style>
/* Estilos del editor (optimizados para Clases) */
.vue-flow__node-classNode {
  /* Anula estilos por defecto molestos */
}
.vue-flow__handle {
  width: 12px;
  height: 12px;
  background: hsl(var(--primary)) !important;
  border-radius: 50%;
  border: 2px solid hsl(var(--background));
}
.vue-flow__edge-path {
  stroke: hsl(var(--primary));
  stroke-width: 2;
}
/* Estilo para los selectores nativos en dark mode */
.dark select {
  background-color: hsl(var(--background));
  color: hsl(var(--foreground));
  border-color: hsl(var(--border));
}

.clip-hexagon {
  clip-path: polygon(10% 0, 90% 0, 100% 50%, 90% 100%, 10% 100%, 0 50%);
}
</style>
